<template>
  <AModal
    :model-value="modelValue"
    size="lg"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <template #title>
      <span class="hsf__title">
        <i class="material-icons-round hsf__title-icon">{{ isEdit ? 'edit_note' : 'note_add' }}</i>
        {{ isEdit ? '섹션 정보 수정' : '홈 섹션 등록' }}
      </span>
    </template>
    <template #sub>
      {{ isEdit
        ? '섹션 자체의 설정만 바꿉니다. 진열할 도서는 섹션 상세 화면에서 다룹니다.'
        : '섹션을 만든 뒤 상세 화면에서 진열할 도서를 담고, 목록에서 노출을 켜세요.' }}
    </template>

    <div class="hsf">
      <div class="hsf__grid">
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">key</i>
            섹션 키 <span class="p-field__req">*</span>
          </label>
          <input
            v-model.trim="form.section_key"
            class="p-input hsf__mono"
            :class="{ 'is-invalid': isDuplicateKey || isInvalidKey }"
            type="text"
            :readonly="isEdit"
            maxlength="60"
            placeholder="예: kong-park-picks"
          />
          <div v-if="isDuplicateKey" class="hsf__err">이미 등록된 섹션 키입니다.</div>
          <div v-else-if="isInvalidKey" class="hsf__err">
            영문 소문자·숫자와 하이픈만 쓸 수 있습니다. (예: topik-picks)
          </div>
          <div v-else class="p-field__hint">
            <i class="material-icons-round">{{ isEdit ? 'lock' : 'info' }}</i>
            {{ isEdit
              ? '앱이 이 키로 섹션을 찾기 때문에 등록 후에는 바꿀 수 없습니다.'
              : '앱이 섹션을 식별하는 값입니다. 등록 후에는 변경할 수 없습니다.' }}
          </div>
        </div>

        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">tag</i>
            노출 개수
          </label>
          <input
            v-model.number="form.display_limit"
            class="p-input"
            type="number"
            min="0"
            max="100"
            placeholder="10"
          />
          <div class="p-field__hint">
            <i class="material-icons-round">info</i>앱 홈에서 한 번에 보여줄 도서 수입니다.
            <b>0을 넣으면 진열한 전부</b>를 내보냅니다.
          </div>
        </div>
      </div>

      <!-- 앱 홈에서 도서 목록을 펼치는 형태. 진열 순서는 네 방식 모두 그대로 따릅니다. -->
      <div class="p-field">
        <label class="p-field__label">
          <i class="material-icons-round p-field__label-icon">dashboard_customize</i>
          노출 방식 <span class="p-field__req">*</span>
        </label>
        <div class="hsf__types">
          <button
            v-for="t in DISPLAY_TYPES"
            :key="t.value"
            type="button"
            :class="['hsf__type', { 'is-on': form.display_type === t.value }]"
            @click="form.display_type = t.value"
          >
            <i class="material-icons-round hsf__type-icon">{{ t.icon }}</i>
            <span class="hsf__type-body">
              <b>{{ t.label }}</b>
              <span>{{ t.desc }}</span>
            </span>
            <i v-if="form.display_type === t.value" class="material-icons-round hsf__type-check">
              check_circle
            </i>
          </button>
        </div>
      </div>

      <!-- 언어별 섹션 타이틀: 앱 홈의 섹션 헤더 문구로 그대로 쓰입니다 -->
      <div class="p-field">
        <label class="p-field__label">
          <i class="material-icons-round p-field__label-icon">translate</i>
          섹션 타이틀 (언어별) <span class="p-field__req">*</span>
        </label>
        <div class="hsf__titles">
          <div v-for="lang in HOME_LANGS" :key="lang.code" class="hsf__title-field">
            <span class="hsf__title-label">
              {{ lang.label }}
              <b v-if="lang.code === 'ko'" class="p-field__req">*</b>
            </span>
            <input
              v-model.trim="form.titles[lang.code]"
              class="p-input"
              type="text"
              maxlength="60"
              :placeholder="lang.code === 'ko' ? '예: 공앤박이 고른 책' : '(선택)'"
            />
          </div>
        </div>
        <div class="p-field__hint">
          <i class="material-icons-round">info</i>비워 둔 언어는 한국어 타이틀로 대신 노출됩니다.
        </div>
      </div>

      <div v-if="!isEdit" class="hsf__notice">
        <i class="material-icons-round">info</i>
        <span>
          새 섹션은 항상 <b>미노출</b> 상태로 만들어집니다.
          도서를 진열한 뒤 목록에서 노출을 켜야 앱 홈에 보입니다.
        </span>
      </div>
    </div>

    <template #foot>
      <ABtn kind="ghost" @click="$emit('update:modelValue', false)">취소</ABtn>
      <ABtn kind="primary" icon="save" :disabled="!canSave" :loading="saving" @click="save">
        {{ isEdit ? '수정 저장' : '등록' }}
      </ABtn>
    </template>
  </AModal>
</template>

<script>
import swal from "sweetalert2";
import AModal from "@/components/Pineapple/AModal.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import { apiErrorMessage, apiErrorStatus } from "@/utils/apiError";
import { HOME_LANGS, DISPLAY_TYPES } from "@/utils/homeSection";

const KEY_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;

const blankTitles = () =>
  HOME_LANGS.reduce((acc, l) => {
    acc[l.code] = "";
    return acc;
  }, {});

export default {
  name: "HomeSectionFormModal",
  components: { AModal, ABtn },
  props: {
    modelValue: { type: Boolean, default: false },
    // 값이 있으면 수정, 없으면 신규 등록
    section: { type: Object, default: null },
    // 섹션 키 중복을 서버 409 전에 걸러내기 위한 기존 목록
    sections: { type: Array, default: () => [] },
  },
  emits: ["update:modelValue", "saved"],
  data() {
    return {
      HOME_LANGS,
      DISPLAY_TYPES,
      saving: false,
      form: {
        section_key: "",
        titles: blankTitles(),
        display_type: "carousel",
        display_limit: 10,
      },
      original: null,
    };
  },
  computed: {
    isEdit() {
      return !!this.section;
    },
    isDuplicateKey() {
      if (this.isEdit || !this.form.section_key) return false;
      return this.sections.some((s) => s.section_key === this.form.section_key);
    },
    isInvalidKey() {
      if (this.isEdit || !this.form.section_key) return false;
      return !KEY_RE.test(this.form.section_key);
    },
    canSave() {
      if (this.saving) return false;
      if (!this.form.section_key || this.isDuplicateKey || this.isInvalidKey) return false;
      return !!this.form.titles.ko;
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.init();
    },
  },
  methods: {
    init() {
      this.saving = false;
      const s = this.section;
      const titles = blankTitles();
      Object.keys(s?.titles || {}).forEach((k) => {
        titles[k] = s.titles[k] || "";
      });
      this.form = {
        section_key: s?.section_key || "",
        titles,
        display_type: s?.display_type || "carousel",
        display_limit: s?.display_limit ?? 10,
      };
      this.original = JSON.parse(JSON.stringify(this.form));
    },
    // 빈 언어는 보내지 않습니다.
    compact(titles) {
      const out = {};
      Object.keys(titles).forEach((k) => {
        if (titles[k]) out[k] = titles[k];
      });
      return out;
    },
    normalizedLimit() {
      const n = Number(this.form.display_limit);
      if (!Number.isFinite(n) || n < 0) return 0;
      return Math.min(100, Math.floor(n));
    },
    async save() {
      if (!this.canSave) return;
      this.saving = true;
      const titles = this.compact(this.form.titles);

      try {
        if (this.isEdit) {
          // PUT 은 변경할 필드만 받습니다. section_key 는 변경 불가라 제외합니다.
          const data = {};
          if (JSON.stringify(titles) !== JSON.stringify(this.compact(this.original.titles))) {
            data.titles = titles;
          }
          if (this.form.display_type !== this.original.display_type) {
            data.display_type = this.form.display_type;
          }
          if (this.normalizedLimit() !== Number(this.original.display_limit)) {
            data.display_limit = this.normalizedLimit();
          }

          if (!Object.keys(data).length) {
            this.$notifyVue({ type: "info", title: "변경된 내용이 없습니다." });
            this.saving = false;
            return;
          }

          await this.$axios({
            method: "put",
            url: `/emp/home/sections/${this.section.idx}`,
            data,
            loadingBar: true,
          });
          this.$notifyVue({ type: "success", title: "섹션 정보를 수정했습니다." });
        } else {
          await this.$axios({
            method: "post",
            url: "/emp/home/sections",
            data: {
              section_key: this.form.section_key,
              titles,
              display_type: this.form.display_type,
              display_limit: this.normalizedLimit(),
            },
            loadingBar: true,
          });
          this.$notifyVue({
            type: "success",
            title: "섹션을 등록했습니다.",
            text: "상세 화면에서 진열할 도서를 담으세요.",
          });
        }

        this.$emit("update:modelValue", false);
        this.$emit("saved");
      } catch (e) {
        console.error("홈 섹션 저장 실패:", e);
        const message = apiErrorMessage(e, "섹션을 저장하지 못했습니다.");
        // 409 = 섹션 키 중복. 위 사전 검사를 통과해도 동시 등록이면 여기로 옵니다.
        if (apiErrorStatus(e) === 409) {
          swal.fire("등록할 수 없습니다", message, "warning");
        } else {
          swal.fire("저장 실패", message, "error");
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.hsf {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 800;
    color: $p-ink;
  }

  &__title-icon {
    color: $p-point-deep;
    font-size: 20px !important;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;

    @media (max-width: 640px) { grid-template-columns: 1fr; }
  }

  &__mono {
    font-family: $p-font-mono;
  }

  &__err {
    margin-top: 5px;
    font-size: 11px;
    font-weight: 700;
    color: $p-err;
  }

  &__types {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;

    @media (max-width: 640px) { grid-template-columns: 1fr; }
  }

  &__type {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 13px 14px;
    text-align: left;
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    cursor: pointer;
    font-family: $p-font-body;
    transition: border-color $p-dur $p-ease, background $p-dur $p-ease;

    &:hover { border-color: $p-line-hard; }

    &.is-on {
      border-color: $p-point-deep;
      background: $p-point-soft;
    }
  }

  &__type-icon {
    font-size: 20px !important;
    color: $p-point-deep;
    flex-shrink: 0;
  }

  &__type-body {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;

    b { font-size: 12px; font-weight: 800; color: $p-ink; }
    span { font-size: 11px; line-height: 1.5; color: $p-ink-mid; }
  }

  &__type-check {
    position: absolute;
    top: 9px;
    right: 10px;
    font-size: 16px !important;
    color: $p-point-deep;
  }

  &__titles {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;

    @media (max-width: 640px) { grid-template-columns: 1fr; }
  }

  &__title-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__title-label {
    font-size: 11px;
    font-weight: 700;
    color: $p-ink-soft;
  }

  &__notice {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    padding: 11px 13px;
    background: $p-info-soft;
    border: 1px solid rgba($p-info, 0.28);
    border-radius: $p-radius-md;
    font-size: 12px;
    line-height: 1.55;
    color: $p-ink;

    > i { font-size: 18px !important; color: $p-info; flex-shrink: 0; }
  }
}
</style>
